import React, { useState } from 'react';
import { Button, Form, Input, Card, Space, Empty, Switch, Select, message, Modal } from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { apiClient, apiErrorMessage } from '../../../utils';
import { FilterSelector } from '../../../elements';
import { TargetSelector, SelectedPermission } from './target-selector';
import { FilterGroup } from '../../../models';

interface PermissionRequest {
  effect: 'ALLOW' | 'DENY';
  target: string;
  filter?: FilterGroup;
  isActive: boolean;
}

export function NewRole2Page(props: { onSaved: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<Map<string, SelectedPermission>>(new Map());
  const [isTargetSelectorVisible, setIsTargetSelectorVisible] = useState(false);
  const [selectedPermissionsSearch, setSelectedPermissionsSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const [form] = Form.useForm();

  // Update permission effect
  const updatePermissionEffect = (targetKey: string, effect: 'ALLOW' | 'DENY') => {
    const newSelections = new Map(selectedPermissions);
    const permission = newSelections.get(targetKey);
    if (permission) {
      newSelections.set(targetKey, { ...permission, effect });
      setSelectedPermissions(newSelections);
    }
  };

  // Update permission filter
  const updatePermissionFilter = (targetKey: string, filter: FilterGroup | undefined) => {
    const newSelections = new Map(selectedPermissions);
    const permission = newSelections.get(targetKey);
    if (permission) {
      newSelections.set(targetKey, { ...permission, filter });
      setSelectedPermissions(newSelections);
    }
  };

  // Toggle filter enabled
  const toggleFilterEnabled = (targetKey: string, enabled: boolean) => {
    const newSelections = new Map(selectedPermissions);
    const permission = newSelections.get(targetKey);
    if (permission) {
      newSelections.set(targetKey, {
        ...permission,
        filterEnabled: enabled,
        filter: enabled ? permission.filter : undefined,
      });
      setSelectedPermissions(newSelections);
    }
  };

  // Remove permission
  const removePermission = (targetKey: string) => {
    const newSelections = new Map(selectedPermissions);
    newSelections.delete(targetKey);
    setSelectedPermissions(newSelections);
  };

  const onFinish = (values: any) => {
    setSubmitting(true);

    if (selectedPermissions.size === 0) {
      message.error('En az bir yetki seçilmelidir.');
      setSubmitting(false);
      return;
    }

    const rolePermissions: PermissionRequest[] = Array.from(selectedPermissions.values()).map(permission => ({
      effect: permission.effect,
      target: permission.target,
      filter: permission.filter,
      isActive: true,
    }));

    const body = {
      name: values.name,
      description: values.description,
      permissions2: rolePermissions,
    };

    apiClient
      .post('authorization/roles', body)
      .then(() => {
        props.onSaved();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Form labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} name="basic" onFinish={onFinish} form={form}>
      <Form.Item
        label="İsim"
        name="name"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          {
            pattern: /^[a-zA-Z0-9ıİğĞüÜşŞöÖçÇ][a-zA-Z0-9_\-ıİğĞüÜşŞöÖçÇ\s]*$/,
            message: 'Sadece harf, rakam, boşluk, - ve _ karakteri kullanılabilir',
          },
          { max: 100, message: 'En fazla 100 karakter' },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item label="Açıklama" name="description" rules={[{ max: 1000, message: 'En fazla 1000 karakter' }]}>
        <Input.TextArea />
      </Form.Item>

      {/* Permission Selector Modal */}
      <Modal
        title="Yetki seç"
        visible={isTargetSelectorVisible}
        onCancel={() => setIsTargetSelectorVisible(false)}
        closable={false}
        destroyOnClose
        footer={[
          <Button key="close" type="primary" onClick={() => setIsTargetSelectorVisible(false)}>
            Tamam
          </Button>,
        ]}
        width="70%"
      >
        <TargetSelector
          onPermissionsChange={setSelectedPermissions}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          expandedKeys={expandedKeys}
          onExpandedKeysChange={setExpandedKeys}
          currentPermissions={selectedPermissions}
        />
      </Modal>

      <Form.Item label="Yetkiler" required labelCol={{ style: { paddingTop: '4px' }, span: 4 }} wrapperCol={{ span: 20 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Selected Permissions List - Always visible */}
          <Card
            title={
              selectedPermissions?.size === 0 ? null : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {selectedPermissions.size > 0 && (
                    <Input
                      placeholder="Ara..."
                      prefix={<SearchOutlined />}
                      value={selectedPermissionsSearch}
                      onChange={e => setSelectedPermissionsSearch(e.target.value)}
                      allowClear
                      size="small"
                      style={{ width: '250px' }}
                      onClick={e => e.stopPropagation()}
                    />
                  )}
                  {selectedPermissions.size > 0 && (
                    <span
                      style={{
                        fontSize: '13px',
                        color: '#595959',
                      }}
                    >{`${selectedPermissions.size} yetki eklendi`}</span>
                  )}
                </div>
              )
            }
            extra={
              selectedPermissions.size === 0 ? null : (
                <Button type="primary" size="small" onClick={() => setIsTargetSelectorVisible(true)}>
                  + Yetki ekle
                </Button>
              )
            }
            size="small"
          >
            {selectedPermissions.size === 0 ? (
              <Empty description="Henüz yetki eklenmedi" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '20px 0' }}>
                <Button type="primary" onClick={() => setIsTargetSelectorVisible(true)}>
                  + Yetki ekle
                </Button>
              </Empty>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {/* Table Header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(350px, 2.5fr) 60px 70px minmax(220px, 2.5fr) 40px',
                    gap: '16px',
                    padding: '10px 16px',
                    background: '#fafafa',
                    borderBottom: '2px solid #e8e8e8',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#666',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <div>Yetki</div>
                  <div style={{ textAlign: 'center' }}>İzin</div>
                  <div style={{ textAlign: 'center' }}>Filtre</div>
                  <div>Filtre seçimi</div>
                  <div></div>
                </div>

                {/* Permission Rows */}
                {Array.from(selectedPermissions.values())
                  .filter(
                    permission =>
                      !selectedPermissionsSearch ||
                      permission.label.toLowerCase().includes(selectedPermissionsSearch.toLowerCase()) ||
                      permission.target.toLowerCase().includes(selectedPermissionsSearch.toLowerCase()),
                  )
                  .map((permission, index) => (
                  <div
                    key={permission.target}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(350px, 2.5fr) 60px 70px minmax(220px, 2.5fr) 40px',
                      gap: '8px',
                      padding: '10px 0',
                      alignItems: 'center',
                      background: index % 2 === 0 ? 'white' : '#fafafa',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                    onMouseLeave={e => (e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#fafafa')}
                  >
                    {/* Permission Name */}
                    <div
                      style={{
                        fontSize: '13px',
                        wordBreak: 'break-word',
                        lineHeight: '1.4',
                        paddingLeft: '8px',
                      }}
                    >
                      {permission.label}
                    </div>

                    {/* Effect Selection - Select Dropdown */}
                    <Select
                      size="small"
                      value={permission.effect}
                      onChange={value => updatePermissionEffect(permission.target, value)}
                      style={{ width: '100%' }}
                      options={[
                        {
                          value: 'ALLOW',
                          label: <span style={{ color: '#52c41a', fontSize: '16px' }}>✓</span>,
                        },
                        {
                          value: 'DENY',
                          label: <span style={{ color: '#ff4d4f', fontSize: '16px' }}>✕</span>,
                        },
                      ]}
                    />

                    {/* Filter Toggle */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      {permission.conditionSchemes && permission.conditionSchemes.length > 0 ? (
                        <Switch
                          size="small"
                          checked={permission.filterEnabled || false}
                          onChange={checked => toggleFilterEnabled(permission.target, checked)}
                        />
                      ) : (
                        <span style={{ fontSize: '11px', color: '#ccc' }}>—</span>
                      )}
                    </div>

                    {/* Filter Selection */}
                    <div style={{ minWidth: 0 }}>
                      {permission.conditionSchemes && permission.conditionSchemes.length > 0 && permission.filterEnabled ? (
                        <FilterSelector
                          value={permission.filter}
                          size="small"
                          dataSetId={permission.dataSetId}
                          onChange={value => updatePermissionFilter(permission.target, value)}
                        />
                      ) : (
                        <span style={{ fontSize: '11px', color: '#ccc' }}>—</span>
                      )}
                    </div>

                    {/* Delete Button */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removePermission(permission.target)}
                        style={{
                          padding: '4px',
                          height: 'auto',
                          minWidth: 'auto',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Space>
      </Form.Item>

      <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Ekle
        </Button>
      </Form.Item>
    </Form>
  );
}
